import type { StreamTargetChunk } from 'mediabunny';

export interface FileTarget {
  readonly writable: WritableStream<StreamTargetChunk>;
  /** Resolves the finished file once the writable has been closed. */
  finish(): Promise<File>;
  discard(): Promise<void>;
}

const EXPORT_DIRECTORY = 'exports';

/**
 * Writes the export straight to the Origin Private File System, so even an hour
 * long 4K export never has to fit in memory. Falls back to in-memory chunks
 * where OPFS sync handles are unavailable.
 */
export async function createFileTarget(name: string, mimeType: string, location?: FileLocation): Promise<FileTarget> {
  try {
    return await createOpfsTarget(name, mimeType, location);
  } catch {
    return createMemoryTarget(name, mimeType);
  }
}

/** A fixed place in OPFS (directory path and file name), replaced if it exists. */
export interface FileLocation {
  readonly directory: readonly string[];
  readonly file: string;
}

/** Exports older than this are leftovers from earlier visits. */
const STALE_AFTER_MS = 6 * 60 * 60 * 1000;

/**
 * Removes exports left over from previous visits. Runs once at startup, not
 * per export, so every result of the current session stays downloadable — and
 * only old files go, because other open tabs share the same storage.
 */
export async function cleanupStaleExports(now = Date.now()): Promise<void> {
  const root = await navigator.storage.getDirectory();
  const directory = await root.getDirectoryHandle(EXPORT_DIRECTORY, { create: true });
  const entries = directory as unknown as { entries(): AsyncIterable<[string, FileSystemHandle]> };
  for await (const [name, handle] of entries.entries()) {
    if (handle.kind !== 'file') continue;
    const file = await (handle as FileSystemFileHandle).getFile().catch(() => null);
    // Files still being written are locked; removal simply fails and is retried next visit.
    if (file && now - file.lastModified > STALE_AFTER_MS) await directory.removeEntry(name).catch(() => undefined);
  }
}

async function createOpfsTarget(name: string, mimeType: string, location?: FileLocation): Promise<FileTarget> {
  let directory = await navigator.storage.getDirectory();
  for (const part of location?.directory ?? [EXPORT_DIRECTORY]) directory = await directory.getDirectoryHandle(part, { create: true });
  const unique = location?.file ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}-${name}`;
  const handle = await directory.getFileHandle(unique, { create: true });
  const access = await handle.createSyncAccessHandle();
  access.truncate(0);
  const writable = new WritableStream<StreamTargetChunk>({
    write(chunk) {
      access.write(chunk.data, { at: chunk.position });
    },
    close() {
      access.flush();
      access.close();
    },
    abort() {
      access.close();
    },
  });
  return {
    writable,
    async finish() {
      const file = await handle.getFile();
      return new File([file], name, { type: mimeType });
    },
    async discard() {
      await directory.removeEntry(handle.name).catch(() => undefined);
    },
  };
}

function createMemoryTarget(name: string, mimeType: string): FileTarget {
  const chunks: Array<{ position: number; data: Uint8Array }> = [];
  let size = 0;
  const writable = new WritableStream<StreamTargetChunk>({
    write(chunk) {
      chunks.push({ position: chunk.position, data: chunk.data.slice() });
      size = Math.max(size, chunk.position + chunk.data.byteLength);
    },
  });
  return {
    writable,
    async finish() {
      const buffer = new Uint8Array(size);
      for (const chunk of chunks) buffer.set(chunk.data, chunk.position);
      return new File([buffer], name, { type: mimeType });
    },
    async discard() {
      chunks.length = 0;
    },
  };
}
